import { ReactNode, Fragment, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../store'
import { testAPI } from '../services/api'
import { clearTest } from '../store/slices/testSlice'
import { resetTimer } from '../store/slices/timerSlice'

const terminalStatuses = new Set(['submitted', 'completed', 'expired', 'cancelled'])

/** Do not mount or reveal test content until this history entry is verified. */
const ActiveTestRoute = ({ children }: { children: ReactNode }) => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const currentSession = useSelector((state: RootState) => state.test.currentSession)
  const routeKey = `${sessionId}:${location.pathname}:${location.key}`
  const [verifiedKey, setVerifiedKey] = useState<string | null>(null)
  const [mountedKey, setMountedKey] = useState<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const locallySubmitted = currentSession !== null && currentSession.session_id === sessionId &&
    terminalStatuses.has(currentSession.status)

  useLayoutEffect(() => {
    let active = true
    let version = 0
    const hide = () => {
      // Hide synchronously before the browser captures a back/forward snapshot.
      if (contentRef.current) contentRef.current.hidden = true
      setVerifiedKey(null)
      version += 1
    }
    const verify = async () => {
      hide()
      const requestVersion = version
      if (!sessionId) {
        navigate('/dashboard', { replace: true })
        return
      }
      try {
        const { data } = await testAPI.getSessionStatus(sessionId)
        if (!active || requestVersion !== version) return
        if (terminalStatuses.has(data.status)) {
          dispatch(clearTest())
          dispatch(resetTimer())
          navigate(`/test/${sessionId}/completed`, { replace: true })
        } else if (['created', 'started', 'paused'].includes(data.status)) {
          setMountedKey(routeKey)
          setVerifiedKey(routeKey)
        } else {
          throw new Error('Unrecognized session status')
        }
      } catch {
        if (!active || requestVersion !== version) return
        dispatch(clearTest())
        dispatch(resetTimer())
        navigate('/dashboard', { replace: true })
      }
    }
    const onVisibility = () => {
      if (document.hidden) hide()
      else void verify()
    }
    const onRestore = () => { void verify() }
    window.addEventListener('pagehide', hide)
    window.addEventListener('pageshow', onRestore)
    window.addEventListener('popstate', onRestore)
    window.addEventListener('focus', onRestore)
    document.addEventListener('visibilitychange', onVisibility)
    void verify()
    return () => {
      active = false
      version += 1
      window.removeEventListener('pagehide', hide)
      window.removeEventListener('pageshow', onRestore)
      window.removeEventListener('popstate', onRestore)
      window.removeEventListener('focus', onRestore)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [sessionId, routeKey, dispatch, navigate])

  const visible = verifiedKey === routeKey && !locallySubmitted
  return (
    <>
      {!visible && <p role="status" className="p-8 text-center text-gray-600">Checking test status...</p>}
      <div ref={contentRef} hidden={!visible}>
        {mountedKey === routeKey && <Fragment key={routeKey}>{children}</Fragment>}
      </div>
    </>
  )
}

export default ActiveTestRoute
